import { Injectable } from "@angular/core"
import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  ConfirmSignUpCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  RespondToAuthChallengeCommand,
  GetUserCommand,
  ResendConfirmationCodeCommand,
} from "@aws-sdk/client-cognito-identity-provider"
import { Observable, from, BehaviorSubject } from "rxjs"
import { map, catchError, switchMap } from "rxjs/operators"
import { throwError } from "rxjs"
import { AuthService } from "./auth.service"
import { PermissionService } from "./permission.service"
import { environment } from "../../environments/environment"

interface CognitoUser {
  username: string
  email: string
  accessToken: string
  idToken: string
  refreshToken: string
  role?: string
  ownerid?: number
  accounttype?: string
  companyname?: string
  textnotifications?: string
}

@Injectable({
  providedIn: "root",
})
export class CognitoService {
  private cognitoClient: CognitoIdentityProviderClient
  private readonly clientId = environment.cognitoClientId
  private readonly region = environment.cognitoRegion

  private currentUserSubject = new BehaviorSubject<CognitoUser | null>(this.getCurrentUserFromStorage())
  public currentUser$ = this.currentUserSubject.asObservable()

  constructor(private authService: AuthService, private permissionService: PermissionService) {
    this.cognitoClient = new CognitoIdentityProviderClient({
      region: this.region,
    })
  }

  private getCurrentUserFromStorage(): CognitoUser | null {
    const accessToken = localStorage.getItem("accessToken")
    const idToken = localStorage.getItem("idToken")
    const refreshToken = localStorage.getItem("refreshToken")
    const userInfo = localStorage.getItem("userInfo")

    if (accessToken && idToken && refreshToken && userInfo) {
      return {
        ...JSON.parse(userInfo),
        accessToken,
        idToken,
        refreshToken,
      }
    }
    return null
  }

  private storeTokens(tokens: any, userInfo: any): void {
    localStorage.setItem("accessToken", tokens.AccessToken)
    localStorage.setItem("idToken", tokens.IdToken)
    localStorage.setItem("refreshToken", tokens.RefreshToken)
    localStorage.setItem("userInfo", JSON.stringify(userInfo))

    const user: CognitoUser = {
      ...userInfo,
      accessToken: tokens.AccessToken,
      idToken: tokens.IdToken,
      refreshToken: tokens.RefreshToken,
    }

    this.currentUserSubject.next(user)
    this.authService.notifySessionChanged()
  }

  private clearTokens(): void {
    localStorage.removeItem("accessToken")
    localStorage.removeItem("idToken")
    localStorage.removeItem("refreshToken")
    localStorage.removeItem("userInfo")
    this.currentUserSubject.next(null)
  }

  confirmSignUp(email: string, confirmationCode: string): Observable<any> {
    console.log("Confirming signup for email:", email)

    const registrationData = this.authService.getRegistrationData()
    const username = registrationData?.username || email // Fallback to email if no registration data

    console.log("Using username for confirmation:", username)

    const command = new ConfirmSignUpCommand({
      ClientId: this.clientId,
      Username: username, // Use cognito_username from registration data
      ConfirmationCode: confirmationCode,
    })

    return from(this.cognitoClient.send(command)).pipe(
      map((response) => {
        console.log("Confirmation successful for:", username)
        this.authService.clearRegistrationData()

        return {
          success: true,
          message: "Email verified successfully. You can now log in.",
        }
      }),
      catchError((error) => {
        console.error("Confirm sign up error:", error)
        return throwError(() => ({
          error: {
            message: this.getErrorMessage(error),
          },
        }))
      }),
    )
  }

  resendConfirmationCode(email: string): Observable<any> {
    console.log("Resending confirmation code for email:", email)

    const registrationData = this.authService.getRegistrationData()
    const username = registrationData?.username || email // Fallback to email if no registration data

    console.log("Using username for resend:", username)

    const command = new ResendConfirmationCodeCommand({
      ClientId: this.clientId,
      Username: username, // Use cognito_username from registration data
    })

    return from(this.cognitoClient.send(command)).pipe(
      map((response) => {
        console.log("Resend successful for:", username)
        return {
          success: true,
          message: "Verification code resent successfully!",
        }
      }),
      catchError((error) => {
        console.error("Resend confirmation code error:", error)
        return throwError(() => ({
          error: {
            message: this.getErrorMessage(error),
          },
        }))
      }),
    )
  }

  signIn(email: string, password: string): Observable<any> {
    const command = new InitiateAuthCommand({
      ClientId: this.clientId,
      AuthFlow: "USER_PASSWORD_AUTH",
      AuthParameters: {
        USERNAME: email,
        PASSWORD: password,
      },
    })

    return from(this.cognitoClient.send(command)).pipe(
      switchMap((response) => {
        if (response.ChallengeName === "NEW_PASSWORD_REQUIRED") {
          return from([
            {
              challengeName: "NEW_PASSWORD_REQUIRED",
              session: response.Session,
              message: "New password required",
            },
          ])
        }

        if (response.AuthenticationResult) {
          return from(
            this.getUserAttributes(response.AuthenticationResult.AccessToken!).then((attributes) => {
              const userInfo = {
                username: email,
                email: email,
                role: attributes["custom:role"] || "user",
                ownerid: attributes["custom:ownerid"] ? Number.parseInt(attributes["custom:ownerid"]) : undefined,
                accounttype: attributes["custom:accounttype"] || "individual",
                companyname: attributes["custom:companyname"] || "",
                textnotifications: attributes["custom:textnotifications"] || "false",
              }

              this.storeTokens(response.AuthenticationResult, userInfo)
              this.permissionService.setUserRole(userInfo.role);
              return {
                success: true,
                tokens: response.AuthenticationResult,
                user: userInfo,
                message: "Login successful",
              }
            }),
          )
        }

        return from([
          {
            error: {
              message: "Login failed",
            },
          },
        ])
      }),
      catchError((error) => {
        console.error("Sign in error:", error)
        return throwError(() => ({
          error: {
            message: this.getErrorMessage(error),
          },
        }))
      }),
    )
  }

  setNewPassword(email: string, newPassword: string, session: string): Observable<any> {
    const command = new RespondToAuthChallengeCommand({
      ClientId: this.clientId,
      ChallengeName: "NEW_PASSWORD_REQUIRED",
      Session: session,
      ChallengeResponses: {
        USERNAME: email,
        NEW_PASSWORD: newPassword,
      },
    })

    return from(this.cognitoClient.send(command)).pipe(
      switchMap((response) => {
        if (!response.AuthenticationResult) {
          return from([{
            error: { message: "Failed to set new password" }
          }])
        }

        // ⭐ After password set → fetch all user attributes
        const accessToken = response.AuthenticationResult.AccessToken!

        return from(
          this.getUserAttributes(accessToken).then((attributes) => {
            const userInfo = {
              username: email,
              email: email,
              role: attributes["custom:role"] || "user",
              ownerid: attributes["custom:ownerid"] ? Number(attributes["custom:ownerid"]) : undefined,
              accounttype: attributes["custom:accounttype"] || "individual",
              companyname: attributes["custom:companyname"] || "",
              textnotifications: attributes["custom:textnotifications"] || "false",
            }

            this.storeTokens(response.AuthenticationResult, userInfo)

            // update permission service
            this.permissionService.setUserRole(userInfo.role)

            return {
              success: true,
              tokens: response.AuthenticationResult,
              user: userInfo,
              message: "Password set successfully",
            }
          })
        )
      }),
      catchError((error) => {
        return throwError(() => ({
          error: {
            message: this.getErrorMessage(error),
          },
        }))
      }),
    )
  }

  forgotPassword(email: string): Observable<any> {
    const command = new ForgotPasswordCommand({
      ClientId: this.clientId,
      Username: email,
    })

    return from(this.cognitoClient.send(command)).pipe(
      map((response) => ({
        success: true,
        message: "Password reset code sent to your email",
      })),
      catchError((error) => {
        console.error("Forgot password error:", error)
        return throwError(() => ({
          error: {
            message: this.getErrorMessage(error),
          },
        }))
      }),
    )
  }

  confirmForgotPassword(email: string, confirmationCode: string, newPassword: string): Observable<any> {
    const command = new ConfirmForgotPasswordCommand({
      ClientId: this.clientId,
      Username: email,
      ConfirmationCode: confirmationCode,
      Password: newPassword,
    })

    return from(this.cognitoClient.send(command)).pipe(
      map((response) => ({
        success: true,
        message: "Password reset successfully",
      })),
      catchError((error) => {
        console.error("Confirm forgot password error:", error)
        return throwError(() => ({
          error: {
            message: this.getErrorMessage(error),
          },
        }))
      }),
    )
  }

  private async getUserAttributes(accessToken: string): Promise<{ [key: string]: string }> {
    try {
      const command = new GetUserCommand({
        AccessToken: accessToken,
      })

      const response = await this.cognitoClient.send(command)
      const attributes: { [key: string]: string } = {}

      response.UserAttributes?.forEach((attr) => {
        if (attr.Name && attr.Value) {
          attributes[attr.Name] = attr.Value
        }
      })

      return attributes
    } catch (error) {
      console.error("Error getting user attributes:", error)
      return {}
    }
  }

  getUserInfo(): Observable<any> {
    const accessToken = localStorage.getItem("accessToken")
    if (!accessToken) {
      return throwError(() => ({ error: { message: "No access token" } }))
    }

    const command = new GetUserCommand({
      AccessToken: accessToken,
    })

    return from(this.cognitoClient.send(command)).pipe(
      map((response) => {
        const attributes =
          response.UserAttributes?.reduce((acc, attr) => {
            if (attr.Name && attr.Value) {
              acc[attr.Name] = attr.Value
            }
            return acc
          }, {} as any) || {}

        return {
          username: response.Username,
          attributes,
          role: attributes["custom:role"] || "user",
          ownerid: attributes["custom:ownerid"] ? Number.parseInt(attributes["custom:ownerid"]) : undefined,
          accounttype: attributes["custom:accounttype"] || "individual",
          companyname: attributes["custom:companyname"] || "",
          textnotifications: attributes["custom:textnotifications"] || "false",
        }
      }),
      catchError((error) => {
        console.error("Get user info error:", error)
        if (error.name === "NotAuthorizedException") {
          this.logout()
        }
        return throwError(() => ({
          error: {
            message: this.getErrorMessage(error),
          },
        }))
      }),
    )
  }

  logout(): void {
    this.authService.logout()
  }

  isLoggedIn(): boolean {
    return !!localStorage.getItem("accessToken")
  }

  getCurrentUser(): CognitoUser | null {
    return this.currentUserSubject.value
  }

  getAccessToken(): string | null {
    return localStorage.getItem("accessToken")
  }

  private getErrorMessage(error: any): string {
    switch (error.name) {
      case "UserNotFoundException":
        return "User not found. Please check your email address."
      case "NotAuthorizedException":
        return "Invalid email or password."
      case "UserNotConfirmedException":
        return "Please verify your email address before logging in."
      case "InvalidPasswordException":
        return "Password does not meet requirements."
      case "UsernameExistsException":
        return "An account with this email already exists."
      case "CodeMismatchException":
        return "Invalid verification code."
      case "ExpiredCodeException":
        return "Verification code has expired. Please request a new one."
      case "LimitExceededException":
        return "Too many attempts. Please try again later."
      case "TooManyRequestsException":
        return "Too many requests. Please try again later."
      case "AliasExistsException":
        return "An account with this email already exists."
      case "InvalidParameterException":
        return "Invalid verification code format."
      default:
        return error.message || "An unexpected error occurred."
    }
  }
}
